import { reactive } from 'vue'
import { api, attachmentsApi } from '@/lib/api'

// Shared logic for previewing note/activity attachments inside the app instead of
// force-downloading them. Images and PDFs open in an in-app modal; everything else
// opens in a new browser tab (the browser renders or downloads it).
//
// Media can't send an auth header, so we mint a short-lived stream token
// (POST /attachments/:id/stream-token) and load via ?t=<token> — the same pattern
// AudioAttachment.vue uses for inline call recordings.

export type AttachmentKind = 'image' | 'pdf' | 'other'

export interface PreviewableAttachment {
  _id: string
  originalName: string
  mime: string
}

export function classifyAttachment(mime: string | undefined | null): AttachmentKind {
  const m = (mime || '').toLowerCase()
  if (m.startsWith('image/')) return 'image'
  if (m === 'application/pdf') return 'pdf'
  return 'other'
}

interface PreviewState {
  open: boolean
  loading: boolean
  kind: AttachmentKind
  url: string | null
  error: string | null
  current: PreviewableAttachment | null
}

// Module-level singleton so a single <AttachmentPreviewModal> mounted in App.vue
// can be driven from anywhere via openAttachment() — no per-call-site wiring.
export const previewState = reactive<PreviewState>({
  open: false,
  loading: false,
  kind: 'other',
  url: null,
  error: null,
  current: null,
})

function apiBaseUrl(): string {
  // Mirror lib/api.ts: VITE_API_URL or fall back to "/api/v1".
  return (import.meta.env.VITE_API_URL as string | undefined) || '/api/v1'
}

async function fetchStreamUrl(id: string): Promise<string> {
  const res = await api.post(`/attachments/${id}/stream-token`)
  const token = res.data.token as string
  return `${apiBaseUrl()}/attachments/${id}/download?t=${encodeURIComponent(token)}`
}

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { message?: string } }; message?: string }
  return err?.response?.data?.message || err?.message || 'Could not load preview'
}

export async function openAttachment(att: PreviewableAttachment): Promise<void> {
  const kind = classifyAttachment(att.mime)

  if (kind === 'other') {
    // Open the tab synchronously to keep the user-gesture (avoids popup blockers
    // after the await), then point it at the token URL once we have it.
    const w = window.open('', '_blank')
    try {
      const url = await fetchStreamUrl(att._id)
      if (w) w.location.href = url
      else window.open(url, '_blank')
    } catch {
      w?.close()
    }
    return
  }

  // image | pdf → in-app modal
  previewState.open = true
  previewState.loading = true
  previewState.kind = kind
  previewState.current = att
  previewState.url = null
  previewState.error = null
  try {
    previewState.url = await fetchStreamUrl(att._id)
  } catch (e: unknown) {
    previewState.error = extractError(e)
  } finally {
    previewState.loading = false
  }
}

// Re-mint the token URL — used by the modal to retry once if the token expires
// mid-view (mirrors AudioAttachment's @error retry).
export async function refreshPreviewUrl(): Promise<void> {
  if (!previewState.current) return
  try {
    previewState.url = await fetchStreamUrl(previewState.current._id)
    previewState.error = null
  } catch (e: unknown) {
    previewState.error = extractError(e)
  }
}

export function closePreview(): void {
  previewState.open = false
  previewState.url = null
  previewState.error = null
  previewState.current = null
}

// Force a real download (blob via the axios client so auth is applied and the
// browser saves the file even when the API is a different origin, where the
// anchor `download` attribute would be ignored).
export async function downloadAttachment(att: PreviewableAttachment): Promise<void> {
  const res = await attachmentsApi.download(att._id)
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = att.originalName
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
